import React, { useEffect, useMemo, useRef, useState } from 'react'
import { View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { upperFirst } from 'lodash'
import { useRoute } from '@react-navigation/native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { IconName, Button } from '@components/base'
import { BottomSheet, CircleIcon, SelectFromActionSheet } from '@components'
import { NotificationEngineReminderDayOfWeek, ReminderKind } from '@src/types'
import { useSnack } from '@src/utils/navigatorContext'
import { useGoBack } from '@src/utils'
import { remindersHoursIntervalSelector } from '@src/selectors/app'
import { AppRouteProp } from '@src/navigation/types'
import { ReminderFormData, ReminderFormInputs } from './ReminderFormInputs'
import {
  buildDayOfWeek,
  buildHoursInterval,
  buildTimeOfDay,
  frequencyKeyByTitle,
  frequencyTitle,
  orderIntervalRanges,
} from './utils'
import { useAvailableReminders } from './hooks'

const AddNewReminderForm = ({ reminderKind }: { reminderKind: ReminderKind }) => {
  const styles = useStyleSheet(themedStyles)
  const showSnack = useSnack()
  const dispatch = useDispatch()
  const goBack = useGoBack()
  const reminderIntervalSettings = useSelector(remindersHoursIntervalSelector)
  const hoursIntervalRanges = orderIntervalRanges(reminderIntervalSettings.glucoseCheckOptions)

  const isSubmitting = useRef(false)

  const defaultValues = useMemo(
    () => ({
      reminderKind: reminderKind.key,
      frequency: frequencyTitle(reminderKind.allowedFrequencies[0].key),
      timeOfDay: moment().format('hh:mmA'),
      dayOfWeek: upperFirst(NotificationEngineReminderDayOfWeek.Monday),
      hoursInterval: hoursIntervalRanges[hoursIntervalRanges.length / 2].text,
    }),
    [reminderKind, hoursIntervalRanges],
  )

  const { control, handleSubmit, reset } = useForm<ReminderFormData>({
    defaultValues,
  })

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reminderKind, reset])

  const onSaveButtonPress = handleSubmit((data: ReminderFormData) => {
    if (isSubmitting.current) {
      return
    }
    isSubmitting.current = true

    const payload = {
      kind: reminderKind.key,
      frequency: frequencyKeyByTitle(data.frequency),
      dayOfWeek: buildDayOfWeek(data),
      timeOfDay: buildTimeOfDay(data),
      hoursInterval: buildHoursInterval(data, hoursIntervalRanges),
      __typename: 'Reminder',
    }
    dispatch({
      type: 'settings/createReminder',
      payload,
      success: () => goBack(),
      failure: (error: any) => {
        if (error?.message) {
          showSnack(error.message, null, 'error')
        }
      },
      complete: () => {
        isSubmitting.current = false
      },
    })
  })

  return (
    <>
      <View style={styles.form}>
        <ReminderFormInputs
          reminderKind={reminderKind}
          control={control}
          hoursIntervalRanges={hoursIntervalRanges}
        />
      </View>
      <Button
        type="primary"
        size="block"
        accessibilityLabel="Add reminder"
        onPress={onSaveButtonPress}
      >
        Save
      </Button>
    </>
  )
}

export const AddReminder = () => {
  const styles = useStyleSheet(themedStyles)
  const route = useRoute<AppRouteProp<'AddReminderModal'>>()
  const { reminderKind: reminderKindParam } = route.params || {}

  const availableReminders = useAvailableReminders()

  const [reminderKind, setReminderKind] = useState(reminderKindParam || availableReminders[0])

  const onTypeChange = (selectedType: string) => {
    setReminderKind(
      availableReminders.find(({ title }) => title === selectedType) || availableReminders[0],
    )
  }

  return (
    <BottomSheet
      header="Add Reminder"
      showDismissButton
      dismissButtonPosition="top"
      dismissButtonText="Cancel"
    >
      <View style={styles.typeDropdownContainer}>
        {!!reminderKind.icon && (
          <CircleIcon name={reminderKind.icon as IconName} style={styles.icon} />
        )}
        <SelectFromActionSheet
          onChange={onTypeChange}
          options={availableReminders.map(({ title }) => title)}
          label="Type"
          title="Select type"
          value={reminderKind.title}
        />
      </View>
      <AddNewReminderForm reminderKind={reminderKind as ReminderKind} />
    </BottomSheet>
  )
}
const themedStyles = StyleService.create({
  form: {
    flex: 1,
    marginBottom: 16,
  },
  typeDropdownContainer: {
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  icon: {
    marginRight: 16,
    padding: 16,
  },
})
